import { useTranslation } from 'react-i18next';
import { Magnetic } from '@/components/Magnetic';
import { Meta } from '@/components/Meta';
import { Reveal } from '@/components/Reveal';
import { studio } from '@/data/studio';

// Demo: a no-backend form provider (Formspree/Web3Forms) drops in here.
const FORM_ENDPOINT = 'https://formspree.io/f/your-id';

export function ContactPage() {
  const { t } = useTranslation();

  return (
    <>
      <Meta title={t('meta.contactTitle')} description={t('meta.contactDesc')} />

      <header className="u-container pt-[22vh] pb-12">
        <p className="eyebrow text-stone">{t('contact.eyebrow')}</p>
        <h1 className="mt-5 font-display text-6xl md:text-8xl">{t('contact.title')}</h1>
        <p className="mt-8 max-w-xl leading-relaxed text-ink-soft">{t('contact.intro')}</p>
      </header>

      <div className="u-container grid gap-14 pb-[16vh] md:grid-cols-12">
        {/* Legible, high-contrast form — the fix for the current site. */}
        <Reveal className="md:col-span-7">
          <form
            action={FORM_ENDPOINT}
            method="POST"
            className="space-y-6 rounded-sm bg-paper-deep p-8 md:p-10"
          >
            <Field name="name" label={t('contact.formName')} type="text" autoComplete="name" />
            <Field name="email" label={t('contact.formEmail')} type="email" autoComplete="email" />
            <Field name="subject" label={t('contact.formSubject')} type="text" />
            <div>
              <label htmlFor="message" className="eyebrow mb-2 block text-stone">
                {t('contact.formMessage')}
              </label>
              <textarea
                id="message"
                name="message"
                rows={5}
                required
                className="w-full resize-none border-b border-ink/30 bg-transparent py-2 text-lg outline-none transition-colors focus:border-ink"
              />
            </div>
            <Magnetic className="inline-block">
              <button
                type="submit"
                className="inline-flex items-center gap-3 rounded-full bg-ink px-8 py-3.5 text-xs uppercase tracking-[0.2em] text-paper transition-opacity hover:opacity-85"
              >
                {t('contact.send')} <span aria-hidden>→</span>
              </button>
            </Magnetic>
          </form>
        </Reveal>

        <aside className="space-y-8 md:col-span-4 md:col-start-9">
          <div>
            <p className="eyebrow mb-3 text-stone">{t('contact.directEmail')}</p>
            <a href={`mailto:${studio.email}`} className="link-line text-lg">
              {studio.email}
            </a>
          </div>
          <div>
            <p className="eyebrow mb-3 text-stone">{t('contact.phone')}</p>
            <a href={`tel:${studio.phoneHref}`} className="link-line text-lg">
              {studio.phone}
            </a>
          </div>
          <div>
            <p className="eyebrow mb-3 text-stone">WhatsApp</p>
            <a href={studio.whatsapp} target="_blank" rel="noopener noreferrer" className="link-line text-lg">
              {t('contact.whatsapp')}
            </a>
          </div>
          <div>
            <p className="eyebrow mb-3 text-stone">{t('contact.address')}</p>
            <p className="text-lg leading-relaxed">{studio.address}</p>
          </div>
        </aside>
      </div>
    </>
  );
}

function Field({
  name,
  label,
  type,
  autoComplete,
}: {
  name: string;
  label: string;
  type: string;
  autoComplete?: string;
}) {
  return (
    <div>
      <label htmlFor={name} className="eyebrow mb-2 block text-stone">
        {label}
      </label>
      <input
        id={name}
        name={name}
        type={type}
        required
        autoComplete={autoComplete}
        className="w-full border-b border-ink/30 bg-transparent py-2 text-lg outline-none transition-colors focus:border-ink"
      />
    </div>
  );
}
